import React from 'react';

/**
 * ProjectKPICards
 * Three KPI cards: Total Projects, Client Projects, In-house Projects.
 * Donut chart for total breakdown, stacked bars for status distribution.
 * Chart segments are clickable to act as quick filters.
 */

// ── Donut SVG helper ──────────────────────────────────────────────────────────
const DonutChart = ({ percentage, label, size = 104 }) => {
    const r = 15.915;
    return (
        <div className="relative" style={{ width: size, height: size }}>
            <svg className="w-full h-full -rotate-90" viewBox="0 0 36 36">
                <circle cx="18" cy="18" r={r} fill="transparent" stroke="var(--kpi-ring-bg, #e2e8f0)" strokeWidth="3" />
                <circle
                    cx="18" cy="18" r={r} fill="transparent"
                    stroke="var(--kpi-ring-fill, #003263)"
                    strokeWidth="3"
                    strokeDasharray={`${percentage} ${100 - percentage}`}
                    strokeDashoffset="0"
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dasharray 0.6s ease' }}
                />
            </svg>
            <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-sm font-bold" style={{ color: 'var(--kpi-ring-fill, #003263)' }}>{label}</span>
            </div>
        </div>
    );
};

// ── Status stacked bar ────────────────────────────────────────────────────────
const StatusBar = ({ inProgress, onHold, completed, total, onSegmentClick }) => {
    if (total === 0) return <div className="h-3 w-full bg-slate-100 dark:bg-slate-800 rounded-full" />;
    const pctActive = Math.round((inProgress / total) * 100);
    const pctHold = Math.round((onHold / total) * 100);
    const pctDone = 100 - pctActive - pctHold;

    return (
        <div className="space-y-2.5">
            <div className="h-3.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex">
                <div
                    className="h-full bg-blue-600 cursor-pointer hover:brightness-110 transition-all"
                    style={{ width: `${pctActive}%` }}
                    title={`${inProgress} In Progress`}
                    onClick={() => onSegmentClick?.('In Progress')}
                />
                <div
                    className="h-full bg-orange-300 cursor-pointer hover:brightness-110 transition-all"
                    style={{ width: `${pctHold}%` }}
                    title={`${onHold} On Hold`}
                    onClick={() => onSegmentClick?.('On Hold')}
                />
                <div
                    className="h-full bg-emerald-400 cursor-pointer hover:brightness-110 transition-all"
                    style={{ width: `${pctDone}%` }}
                    title={`${completed} Completed`}
                    onClick={() => onSegmentClick?.('Completed')}
                />
            </div>
            <div className="flex justify-between text-xs font-bold text-slate-500 dark:text-slate-400">
                <span className="flex items-center gap-1.5 cursor-pointer hover:text-blue-600 transition-colors" onClick={() => onSegmentClick?.('In Progress')}>
                    <span className="w-2 h-2 rounded-full bg-blue-600" /> {inProgress} In Progress
                </span>
                <span className="flex items-center gap-1.5 cursor-pointer hover:text-orange-500 transition-colors" onClick={() => onSegmentClick?.('On Hold')}>
                    <span className="w-2 h-2 rounded-full bg-orange-300" /> {onHold} Hold
                </span>
                <span className="flex items-center gap-1.5 cursor-pointer hover:text-emerald-600 transition-colors" onClick={() => onSegmentClick?.('Completed')}>
                    <span className="w-2 h-2 rounded-full bg-emerald-400" /> {completed} Done
                </span>
            </div>
        </div>
    );
};

export default function ProjectKPICards({ stats, onQuickFilter }) {
    const {
        total = 0,
        clientCount = 0,
        inhouseCount = 0,
        clientByStatus = {},
        inhouseByStatus = {},
    } = stats || {};

    const clientPct = total > 0 ? Math.round((clientCount / total) * 100) : 0;

    const handleSegmentClick = (type, status) => {
        onQuickFilter?.({ type, status });
    };

    return (
        <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 pb-4">
            {/* KPI 1: Total Projects */}
            <div
                className="bg-white dark:bg-[#1f1f1f] p-6 rounded-2xl border border-slate-200 dark:border-[#303030] flex items-center justify-between hover:shadow-md transition-shadow cursor-pointer"
                style={{ boxShadow: '0px 8px 24px rgba(25, 28, 29, 0.06)' }}
                onClick={() => onQuickFilter?.({ type: 'All', status: null })}
            >
                <div className="space-y-1">
                    <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Total Projects</p>
                    <p className="text-4xl font-bold text-[#003263] dark:text-[#a7c8ff]" style={{ fontFamily: 'Manrope, sans-serif' }}>{total}</p>
                    <div className="flex flex-col gap-1.5 mt-4">
                        <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={(e) => { e.stopPropagation(); onQuickFilter?.({ type: 'Client', status: null }); }}>
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-600" />
                            <span className="text-xs font-bold text-slate-600 dark:text-slate-300">Client: {clientCount} ({clientPct}%)</span>
                        </div>
                        <div className="flex items-center gap-2 cursor-pointer hover:opacity-80 transition-opacity" onClick={(e) => { e.stopPropagation(); onQuickFilter?.({ type: 'In-house', status: null }); }}>
                            <span className="w-2.5 h-2.5 rounded-full bg-blue-200" />
                            <span className="text-xs font-bold text-slate-600 dark:text-slate-300">In-house: {inhouseCount} ({100 - clientPct}%)</span>
                        </div>
                    </div>
                </div>
                <DonutChart percentage={clientPct} label={`${clientPct}%`} />
            </div>

            {/* KPI 2: Client Projects */}
            <div
                className="bg-white dark:bg-[#1f1f1f] p-6 rounded-2xl border border-slate-200 dark:border-[#303030] hover:shadow-md transition-shadow"
                style={{ boxShadow: '0px 8px 24px rgba(25, 28, 29, 0.06)' }}
            >
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Client Projects</p>
                        <p className="text-4xl font-bold text-[#003263] dark:text-[#a7c8ff]" style={{ fontFamily: 'Manrope, sans-serif' }}>{clientCount}</p>
                    </div>
                    <span className="material-symbols-outlined text-blue-600 bg-blue-100/50 dark:bg-blue-900/30 p-2.5 rounded-xl text-2xl">business_center</span>
                </div>
                <StatusBar
                    inProgress={(clientByStatus['In Progress'] || 0) + (clientByStatus.Active || 0)}
                    onHold={clientByStatus['On Hold'] || 0}
                    completed={(clientByStatus.Completed || 0) + (clientByStatus.Closed || 0) + (clientByStatus.Close || 0)}
                    total={clientCount}
                    onSegmentClick={(status) => handleSegmentClick('Client', status)}
                />
            </div>

            {/* KPI 3: In-house Projects */}
            <div
                className="bg-white dark:bg-[#1f1f1f] p-6 rounded-2xl border border-slate-200 dark:border-[#303030] hover:shadow-md transition-shadow"
                style={{ boxShadow: '0px 8px 24px rgba(25, 28, 29, 0.06)' }}
            >
                <div className="flex justify-between items-start mb-4">
                    <div>
                        <p className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">In-house Projects</p>
                        <p className="text-4xl font-bold text-[#003263] dark:text-[#a7c8ff]" style={{ fontFamily: 'Manrope, sans-serif' }}>{inhouseCount}</p>
                    </div>
                    <span className="material-symbols-outlined text-blue-600 bg-blue-100/50 dark:bg-blue-900/30 p-2.5 rounded-xl text-2xl">home_work</span>
                </div>
                <StatusBar
                    inProgress={(inhouseByStatus['In Progress'] || 0) + (inhouseByStatus.Active || 0)}
                    onHold={inhouseByStatus['On Hold'] || 0}
                    completed={(inhouseByStatus.Completed || 0) + (inhouseByStatus.Closed || 0) + (inhouseByStatus.Close || 0)}
                    total={inhouseCount}
                    onSegmentClick={(status) => handleSegmentClick('In-house', status)}
                />
            </div>
        </section>
    );
}
